import React, {
  createContext, useContext, useState, useEffect, useMemo, useCallback
} from 'react';
import PropTypes from 'prop-types';
import { SWEEPSTAKES_CONSTANTS } from '../core/Constants';
import { windowExists, gotoLogonModule } from '../common/utils/AccountUtils';
import { useSweepstakesData } from './SweepstakesDataContext';

const SweepstakesDrawerContext = createContext();

export const useSweepstakesDrawer = () => {
  const context = useContext(SweepstakesDrawerContext);
  if (!context) {
    throw new Error('useSweepstakesDrawer must be used within a SweepstakesDrawerProvider');
  }
  return context;
};

const defaultDrawerState = {
  isOpen: false,
  drawerType: null,
  selectedStep: null,
  sweepstakesData: null,
};

// Resolves the effective drawer type for both normal interactions and deep-link entry.
// Normal flow: requestedType is explicit (OPT_IN, DETAILS, SURVEY, STEP, UNAUTHENTICATED).
// Deep-link flow: requestedType is null — drawer type is inferred from user/challenge state.
const determineDrawerType = (requestedType, isChallengeEnded, sweepstakesData) => {
  // Challenge ended overrides all states except unauthenticated (handled below)
  if (isChallengeEnded) {
    return SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.CHALLENGE_ENDED;
  }

  // Unauthenticated state is always preserved as-is (set before auth check in deep-link flow)
  if (requestedType === SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.UNAUTHENTICATED) {
    return requestedType;
  }

  // Redirect pre-opt-in users to challenge ended when challenge is expired
  if (
    requestedType === SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.OPT_IN
    && sweepstakesData?.challengeStatus === 'EXPIRED'
  ) {
    return SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.CHALLENGE_ENDED;
  }

  // Deep-link flow: no explicit type provided, resolve based on opt-in state
  if (!requestedType) {
    if (sweepstakesData?.challengeStatus === 'EXPIRED' && !sweepstakesData?.isOptedIn) {
      return SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.CHALLENGE_ENDED;
    }
    return sweepstakesData?.isOptedIn
      ? SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS
      : SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.OPT_IN;
  }

  return requestedType;
};

// Utility functions
const isUserAuthenticated = () => {
  if (typeof window === 'undefined') return true;
  const jwt = window?.THDCustomer?.default?.jwt;
  return jwt && jwt.trim() !== '';
};

const clearDrawerUrlParams = () => {
  if (windowExists() && window.location.search.includes('drawerList=sweepstakes')) {
    const url = new URL(window.location);
    url.searchParams.delete('drawerList');
    window.history.replaceState({}, '', url.toString());
  }
};

const getDrawerListParam = () => {
  if (!windowExists()) return null;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('drawerList');
};

const createEventHandlers = (closeDrawer, openDrawerWithAuth, handleSurveyCompletion) => {
  const handleOptInDrawer = (eventData) => openDrawerWithAuth(SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.OPT_IN, eventData);
  const handleDetailsDrawer = (eventData) => {
    return openDrawerWithAuth(SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS, eventData);
  };
  const handleSurveyDrawer = (eventData) => openDrawerWithAuth(SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.SURVEY, eventData);
  const handleStepDrawer = (eventData) => openDrawerWithAuth(SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.STEP, eventData);
  const handleNavigate = (eventData, setDrawerState) => setDrawerState((prev) => ({
    ...prev,
    drawerType: eventData.drawerType,
    selectedStep: eventData.selectedStep || null,
  }));

  return {
    'close-sweepstakes-drawer': closeDrawer,
    'open-sweepstakes-opt-in-drawer': handleOptInDrawer,
    'open-sweepstakes-details-drawer': handleDetailsDrawer,
    'open-sweepstakes-survey-drawer': handleSurveyDrawer,
    'open-sweepstakes-step-drawer': handleStepDrawer,
    'navigate-sweepstakes-drawer': handleNavigate,
    'sweepstakes-survey-completed': handleSurveyCompletion
  };
};

const EVENT_TYPES = [
  'open-sweepstakes-opt-in-drawer',
  'open-sweepstakes-details-drawer',
  'open-sweepstakes-survey-drawer',
  'open-sweepstakes-step-drawer',
  'navigate-sweepstakes-drawer',
  'close-sweepstakes-drawer',
  'sweepstakes-survey-completed',
];

export const SweepstakesDrawerProvider = ({ children }) => {
  const [drawerState, setDrawerState] = useState(defaultDrawerState) || {};

  // Access sweepstakes data to handle deep linking when data loads
  const {
    sweepstakesData,
    isLoading,
    isChallengeEnded,
    updateSweepstakesData
  } = useSweepstakesData();

  const [deepLinkPending, setDeepLinkPending] = useState(false);
  const [drawerListParam, setDrawerListParam] = useState(() => getDrawerListParam());

  useEffect(() => {
    if (!windowExists()) return undefined;
    const onPopState = () => setDrawerListParam(getDrawerListParam());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const openDrawer = useCallback((drawerType, options = {}) => {
    if (!isUserAuthenticated()) {
      gotoLogonModule();
      return;
    }

    const resolvedDrawerType = determineDrawerType(drawerType, isChallengeEnded, options.sweepstakesData);

    setDrawerState({
      isOpen: true,
      drawerType: resolvedDrawerType,
      selectedStep: options.selectedStep || null,
      sweepstakesData: options.sweepstakesData || null,
    });
  }, [isChallengeEnded]);

  const closeDrawer = useCallback(() => {
    setDrawerState(defaultDrawerState);
    clearDrawerUrlParams();
    setDrawerListParam(null);
    setDeepLinkPending(false);
  }, []);

  const navigateToDrawer = useCallback((drawerType, options = {}) => {
    const resolvedDrawerType = determineDrawerType(drawerType, isChallengeEnded);

    setDrawerState((prev) => ({
      ...prev,
      drawerType: resolvedDrawerType,
      selectedStep: options.selectedStep || null,
    }));
  }, [isChallengeEnded]);

  const handleStepBack = useCallback(() => {
    navigateToDrawer(SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS);
  }, [navigateToDrawer]);

  const openDrawerWithAuth = useCallback((drawerType, eventData) => {
    if (!isUserAuthenticated()) {
      gotoLogonModule();
      return;
    }
    const resolvedDrawerType = determineDrawerType(drawerType, isChallengeEnded, eventData.sweepstakesData);

    setDrawerState({
      isOpen: true,
      drawerType: resolvedDrawerType,
      selectedStep: eventData.selectedStep || null,
      sweepstakesData: eventData.sweepstakesData || null,
    });
  }, [isChallengeEnded]);

  const handleSurveyCompletion = useCallback(() => {
    const newCompletedSteps = (sweepstakesData?.completedSteps || 0) + 1;
    const totalSteps = sweepstakesData?.totalSteps || 4;

    const completionUpdates = {
      ctaTitle: null,
      isOptedIn: true,
      completedSteps: newCompletedSteps,
      status: `${newCompletedSteps} of ${totalSteps} Earned`,
      steps: sweepstakesData?.steps?.map((step, index) => {
        if (index === 0) { // TODO: update criterion since step fields have been changed
          return { ...step, completed: true };
        }
        return step;
      }) || []
    };

    if (updateSweepstakesData) {
      updateSweepstakesData(completionUpdates);
    }

    setDrawerState((prev) => ({
      ...prev,
      drawerType: SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS
    }));
  }, [sweepstakesData, updateSweepstakesData]);

  const eventHandlers = useMemo(() => {
    return createEventHandlers(closeDrawer, openDrawerWithAuth, handleSurveyCompletion);
  }, [closeDrawer, openDrawerWithAuth, handleSurveyCompletion]);

  const handleDrawerEvent = React.useCallback((event, data = {}) => {
    const eventName = typeof event === 'string' ? event : event.eventName;
    const eventData = typeof event === 'string' ? data : (event.output || {});

    const handler = eventHandlers[eventName];
    if (handler) {
      if (eventName === 'navigate-sweepstakes-drawer') {
        handler(eventData, setDrawerState);
      } else {
        handler(eventData);
      }
    }
  }, [eventHandlers]);

  useEffect(() => {
    if (drawerListParam !== 'sweepstakes') return;

    if (!isUserAuthenticated()) {
      setDrawerState({
        isOpen: true,
        drawerType: SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.UNAUTHENTICATED,
        selectedStep: null,
        sweepstakesData: null,
      });
      setDrawerListParam(null);
      return;
    }

    setDeepLinkPending(true);
  }, [drawerListParam]);

  useEffect(() => {
    if (!deepLinkPending || isLoading) return;

    const targetDrawerType = determineDrawerType(null, isChallengeEnded, sweepstakesData);

    setDrawerState({
      isOpen: true,
      drawerType: targetDrawerType,
      selectedStep: null,
      sweepstakesData,
    });
    setDeepLinkPending(false);
  }, [deepLinkPending, isLoading, isChallengeEnded, sweepstakesData]);

  useEffect(() => {
    EVENT_TYPES.forEach((eventType) => {
      if (windowExists()) {
        const fullEventName = `loyalty-benefits.${eventType}`;
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on(fullEventName, handleDrawerEvent);
      }
    });

    return () => {
      EVENT_TYPES.forEach((eventType) => {
        if (windowExists()) {
          window.LIFE_CYCLE_EVENT_BUS.lifeCycle.off(`loyalty-benefits.${eventType}`);
        }
      });
    };
  }, [handleDrawerEvent]);

  const contextValue = useMemo(() => ({
    drawerState,
    openDrawer,
    closeDrawer,
    navigateToDrawer,
    handleStepBack,
    DRAWER_TYPES: SWEEPSTAKES_CONSTANTS.DRAWER_TYPES,
  }), [drawerState, openDrawer, closeDrawer, navigateToDrawer, handleStepBack]);

  return (
    <SweepstakesDrawerContext.Provider value={contextValue}>
      {children}
    </SweepstakesDrawerContext.Provider>
  );
};

SweepstakesDrawerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
